<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { ChevronRight } from "lucide-svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Goal from "$app/Pages/Plans/Onboarding/components/Goal.svelte"
  import { appStore } from "$app/store.svelte"

  let loaded = $state(false)
  let saving = $state(false)
  let completed = $state(true)

  let long_term_goal = $state({} as GoalType)
  let annual_goal = $state({} as GoalType)
  let quarterly_goal = $state({} as GoalType)
  let task = $state({} as TaskType)

  let errors: Record<string, any> = $state({})

  async function getGoalsAndTask(): Promise<void> {
    loaded = false
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=task`
      )

      long_term_goal = resp.data.long_term_goal
      annual_goal = resp.data.annual_goal
      quarterly_goal = resp.data.quarterly_goal
      task = resp.data.task
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your goals", "error")
    } finally {
      loaded = true
    }
  }

  async function nextStep(): Promise<void> {
    saving = true
    try {
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.plan.id}/review_goals.json}`
      )

      appStore.plan = resp.data.plan
      appStore.onboarding = resp.data.onboarding
    } catch (e: any) {
      console.error(e)
      if (e.response.data) {
        errors = e.response.data
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      saving = false
    }
  }

  onMount(() => {
    getGoalsAndTask()
  })
</script>

<h2 class="text-2xl text-center text-white">Guided Initial Goals Set Up</h2>
<h3 class="mt-2 text-xl font-bold text-center text-green-500">
  <span class="text-2xl text-bold mr-1">5. </span>
  Goals Review
</h3>

<div class="max-w-3xl mx-auto">
  <h3 class="mt-4 text-2xl text-white mb-8">
    You have successfully created the first goal hierarchy of your plan! From
    the top-level Long-Term goal, down to an individual Task.
  </h3>

  <p class="mt-8 text-white text-lg">
    In Lead With Purpose, Goals and Tasks are all organized within a
    hierarchical structure with the Long-Term goals at the top. As you continue
    to build out your plan, we recommend that you create <span class="font-bold"
      >4 or 5 Long-Term Goals
    </span>. Under each of your Long-Term Goals you will then build out your
    Annual Goals, Quarterly Goals and Tasks.
  </p>
  {#if loaded}
    <div class="scale-75">
      <div style="transform: translateX(-100px)">
        <h2 class="text-2xl pb-4 font-semibold text-white">
          {appStore.plan.name} Initial Goals
        </h2>

        <ul class="hierarchy">
          <li>
            <Goal name={long_term_goal.name} goalType="long_term_goal" />
            <ul>
              <li>
                <Goal name={annual_goal.name} goalType="annual_goal" />
                <ul>
                  <li>
                    <Goal
                      name={quarterly_goal.name}
                      goalType="quarterly_goal"
                    />
                    <ul>
                      <li>
                        <Goal name={task.name} goalType="task" />
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>

    <div class="flex justify-center gap-x-5">
      <Button
        onclick={nextStep}
        disabled={!completed}
        processing={saving}
        class="btn btn-rounded success btn-big flex items-center gap-1"
      >
        <span class="font-light text-core"> COMPLETE INITIAL GOAL SETUP </span>
        <ChevronRight class="size-6 text-core" />
      </Button>
    </div>
  {/if}
</div>
